import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';
import { createPublicEntity, getPublicEntity, updatePublicEntity } from '../../services/publicEntityService';
import { getCities } from '../../services/cityService';
import { CityResponse } from '../../types/city';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import SelectComponent from 'react-select';

interface PublicEntityFormState {
  name: string;
  city_id: string;
}

const PublicEntityForm: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const isEditMode = Boolean(entityId);
  const [formData, setFormData] = useState<PublicEntityFormState>({
    name: '',
    city_id: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [cities, setCities] = useState<CityResponse[]>([]);
  const [selectedCity, setSelectedCity] = useState<{ value: string; label: string } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchCities = async () => {
    try {
      const data = await getCities();
      setCities(data);
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      swal('Erro', 'Não foi possível buscar as cidades.', 'error');
    }
  };

  const fetchEntity = async () => {
    setLoading(true);
    try {
      if (entityId) {
        const data = await getPublicEntity(entityId);
        setFormData({
          name: data.name,
          city_id: data.city.id,
        });
        setSelectedCity({ value: data.city.id, label: data.city.name });
      }
    } catch (error) {
      console.error('Erro ao buscar entidade pública:', error);
      swal('Erro', 'Não foi possível buscar os detalhes da entidade pública.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
    if (isEditMode && entityId) {
      fetchEntity();
    }
  }, [isEditMode, entityId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCityChange = (selectedOption: any) => {
    setSelectedCity(selectedOption);
    setFormData((prev) => ({ ...prev, city_id: selectedOption ? selectedOption.value : '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validação básica
    if (!formData.name || !formData.city_id) {
      swal('Erro', 'Nome e Cidade são obrigatórios.', 'error');
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && entityId) {
        // Atualizar entidade pública
        await updatePublicEntity(entityId, formData);
        swal('Sucesso', 'Entidade pública atualizada com sucesso.', 'success');
      } else {
        // Criar nova entidade pública
        await createPublicEntity(formData);
        swal('Sucesso', 'Entidade pública criada com sucesso.', 'success');
      }
      navigate('/entidades-publicas');
    } catch (error) {
      console.error('Erro ao salvar entidade pública:', error);
      const message = isEditMode ? 'Não foi possível atualizar a entidade pública.' : 'Não foi possível criar a entidade pública.';
      swal('Erro', message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Verificar se o usuário tem permissão
  if (!user?.is_system_admin) {
    return (
      <Typography variant="h6" color="error">
        Você não tem permissão para acessar esta página.
      </Typography>
    );
  }

  const cityOptions = cities.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Editar Entidade Pública' : 'Adicionar Entidade Pública'}
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
          />
          <Box sx={{ mt: 2, mb: 2 }}>
            <SelectComponent
              options={cityOptions}
              isClearable={false}
              placeholder="Selecione a Cidade..."
              value={selectedCity}
              onChange={handleCityChange}
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/entidades-publicas')} sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default PublicEntityForm;